import { useState } from "react";
import { useUpdateInventoryStatus } from "../../hooks/useInventory";
import { Button } from "@tonic/shared/components/ui";
import { Label } from "@tonic/shared/components/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@tonic/shared/components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@tonic/shared/components/ui";
import { Badge } from "@tonic/shared/components/ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@tonic/shared/components/ui";
import { ArrowRight, Recycle, Wrench, Droplet } from "lucide-react";
import PropTypes from 'prop-types';

/**
 * @typedef {'new'|'in_recommerce'|'available'|'sold'|'damaged'} InventoryStatus
 */

/**
 * @typedef {Object} InventoryItem
 * @property {string} id - Item ID
 * @property {string} name - Item name
 * @property {InventoryStatus} status - Current status
 * @property {Object} reCommerceAttributes - Re-commerce attributes
 * @property {boolean} reCommerceAttributes.eligibleForRepair - Whether item is eligible for repair
 * @property {boolean} reCommerceAttributes.eligibleForRefill - Whether item is eligible for refill
 * @property {boolean} reCommerceAttributes.eligibleForResale - Whether item is eligible for resale
 */

/**
 * @typedef {Object} UpdateStatusDialogProps
 * @property {InventoryItem} item - The inventory item to update
 * @property {boolean} open - Whether the dialog is open
 * @property {(open: boolean) => void} onOpenChange - Callback when dialog open state changes
 */

/**
 * Dialog component for updating inventory item status
 * @param {UpdateStatusDialogProps} props
 * @returns {JSX.Element} The update status dialog component
 */
export function UpdateStatusDialog({ item, open, onOpenChange }) {
  const updateStatus = useUpdateInventoryStatus(item.id);
  const [selectedStatus, setSelectedStatus] = useState(item.status);
  const [selectedFlow, setSelectedFlow] = useState(null);

  const availableStatuses = ["new", "in_recommerce", "available", "sold", "damaged"];

  const reCommerceFlows = [
    {
      id: "repair",
      title: "Repair",
      description: "Send item for repair and refurbishment",
      icon: Wrench,
      available: item.reCommerceAttributes.eligibleForRepair,
    },
    {
      id: "refill",
      title: "Refill",
      description: "Refill consumable product",
      icon: Droplet,
      available: item.reCommerceAttributes.eligibleForRefill,
    },
    {
      id: "resale",
      title: "Resale",
      description: "Process item for resale",
      icon: Recycle,
      available: item.reCommerceAttributes.eligibleForResale,
    },
  ];

  /**
   * Handles form submission to update the item status
   * @param {React.FormEvent} e - Form event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateStatus.mutateAsync({
        status: selectedStatus,
        reCommerceFlow: selectedFlow,
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const showReCommerceFlows =
    selectedStatus === "in_recommerce" &&
    reCommerceFlows.some((flow) => flow.available);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Update Status</DialogTitle>
            <DialogDescription>
              Change the status of this inventory item.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            <div className="space-y-2">
              <Label>Current Status</Label>
              <div>
                <Badge>{item.status.replace("_", " ")}</Badge>
              </div>
            </div>
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select
                value={selectedStatus}
                onValueChange={(value) => {
                  setSelectedStatus(value);
                  setSelectedFlow(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  {availableStatuses.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status.replace("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {showReCommerceFlows && (
              <div className="space-y-2">
                <Label>Re-Commerce Flow</Label>
                <div className="grid gap-4">
                  {reCommerceFlows
                    .filter((flow) => flow.available)
                    .map((flow) => {
                      const Icon = flow.icon;
                      return (
                        <Card
                          key={flow.id}
                          className={`cursor-pointer transition-colors ${
                            selectedFlow === flow.id
                              ? "border-primary"
                              : "hover:border-primary/50"
                          }`}
                          onClick={() => setSelectedFlow(flow.id)}
                        >
                          <CardHeader className="flex flex-row items-center space-y-0">
                            <div className="flex items-center space-x-2">
                              <Icon className="h-4 w-4" />
                              <CardTitle className="text-base">
                                {flow.title}
                              </CardTitle>
                            </div>
                            {selectedFlow === flow.id && (
                              <ArrowRight className="ml-auto h-4 w-4" />
                            )}
                          </CardHeader>
                          <CardContent>
                            <CardDescription>{flow.description}</CardDescription>
                          </CardContent>
                        </Card>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                updateStatus.isLoading ||
                selectedStatus === item.status ||
                (showReCommerceFlows && !selectedFlow)
              }
            >
              {updateStatus.isLoading ? "Updating..." : "Update Status"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

UpdateStatusDialog.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['new', 'in_recommerce', 'available', 'sold', 'damaged']).isRequired,
    reCommerceAttributes: PropTypes.shape({
      eligibleForRepair: PropTypes.bool.isRequired,
      eligibleForRefill: PropTypes.bool.isRequired,
      eligibleForResale: PropTypes.bool.isRequired,
    }).isRequired,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
}; 